import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  PictureAsPdf as PdfIcon,
  Refresh,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import api from "../../../api";
import WebSimilaritySection from "./WebSimilaritySection";

/** Colour for a similarity percentage — shared with the recent-imports chip. */
export const similarityColors = (percent, thresholds = { warn: 20, high: 50 }) => {
  if (percent == null) return { bgcolor: "#f5f5f5", color: "#616161", bar: "inherit" };
  if (percent >= thresholds.high) return { bgcolor: "#ffebee", color: "#b71c1c", bar: "error" };
  if (percent >= thresholds.warn) return { bgcolor: "#fff3e0", color: "#e65100", bar: "warning" };
  return { bgcolor: "#e8f5e9", color: "#1b5e20", bar: "success" };
};

/** Similarity range filter for admin lists (maps to similarity_min / similarity_max / similarity params). */
export const SIMILARITY_FILTER_OPTIONS = [
  { value: "", label: "All similarity" },
  { value: "lt10", label: "Below 10%", params: { similarity_max: 10 } },
  { value: "lt30", label: "Below 30%", params: { similarity_max: 30 } },
  { value: "30-50", label: "30% – 50%", params: { similarity_min: 30, similarity_max: 50 } },
  { value: "gte50", label: "50% and above", params: { similarity_min: 50 } },
  { value: "gte80", label: "80% and above", params: { similarity_min: 80 } },
  { value: "unchecked", label: "Not checked", params: { similarity: "unchecked" } },
];

/** Append the API params for a SIMILARITY_FILTER_OPTIONS value to URLSearchParams. */
export const appendSimilarityParams = (params, value) => {
  const opt = SIMILARITY_FILTER_OPTIONS.find((o) => o.value === value);
  Object.entries(opt?.params || {}).forEach(([k, v]) => params.append(k, v));
};

/**
 * Similarity chip for an import row. Shows every state so a missing score is visible:
 * pending → "Checking similarity…", done → "Similarity NN%", failed, or "Not checked".
 */
export const SimilarityChip = ({ job, onClick, height = 17, fontSize = "0.6rem" }) => {
  const status = job?.similarity_status;
  const percent = job?.similarity_percent != null ? Number(job.similarity_percent) : null;
  const base = { height, fontSize, fontWeight: 600, cursor: "pointer" };

  let label;
  let title;
  let sx;
  if (status === "pending") {
    label = "Checking similarity…";
    title = "Plagiarism check is running";
    sx = { bgcolor: "#f5f5f5", color: "#616161" };
  } else if (status === "done" && percent != null) {
    const c = similarityColors(percent);
    label = `Similarity ${percent.toFixed(0)}%`;
    title = job?.similarity_original_percent != null
      ? `${percent.toFixed(0)}% overlaps existing papers (${Number(job.similarity_original_percent).toFixed(0)}% from its original). Click for details.`
      : "Share of this paper's questions that already exist in other imported papers. Click for details.";
    sx = { bgcolor: c.bgcolor, color: c.color };
  } else if (status === "failed") {
    label = "Similarity failed";
    title = "The plagiarism check failed. Click to recheck.";
    sx = { bgcolor: "#ffebee", color: "#b71c1c" };
  } else {
    label = "Similarity: not checked";
    title = status === "skipped"
      ? "No extracted questions or text to compare yet."
      : "No plagiarism check has run for this import yet. Click to run one.";
    sx = { bgcolor: "transparent", color: "#9e9e9e", border: "1px dashed #bdbdbd" };
  }

  return (
    <Tooltip title={title}>
      <Chip label={label} size="small" onClick={onClick} sx={{ ...base, ...sx }} />
    </Tooltip>
  );
};

const SourceRow = ({ source, thresholds, onOpenPdf, onReview }) => {
  const [open, setOpen] = useState(false);
  const c = similarityColors(source.percent, thresholds);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          {source.spans?.length > 0 && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
            {source.filename || `Import #${source.import_id}`}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
            <Chip size="small" variant="outlined" label={`Import #${source.import_id}`} sx={{ height: 17, fontSize: "0.6rem", fontFamily: "monospace" }} />
            {source.exact_file_match && (
              <Chip size="small" color="error" label="Identical PDF" sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600 }} />
            )}
            {source.status && (
              <Chip size="small" label={source.status} sx={{ height: 17, fontSize: "0.6rem" }} />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {source.category || "—"}
            {source.created_at && <><br />{format(new Date(source.created_at), "d MMM yyyy")}</>}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip
            size="small"
            label={`${Number(source.percent).toFixed(1)}%`}
            sx={{ fontWeight: 700, bgcolor: c.bgcolor, color: c.color }}
          />
          {source.exact_file_match && source.content_percent != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              questions {Number(source.content_percent).toFixed(0)}% alike
            </Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <Tooltip title="Open matching PDF">
            <IconButton size="small" onClick={() => onOpenPdf(source.import_id)}>
              <PdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {["preview", "completed", "confirmed"].includes(source.status) && (
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => onReview(source.import_id)}>
              Review
            </Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Longest matching passages ({source.shared_fingerprints} shared fingerprints). Text is shown normalised.
              </Typography>
              {source.spans.map((span, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1, bgcolor: "#fffde7", borderColor: "#fff59d" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {span.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">~{span.words} words</Typography>
                </Paper>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const QuestionPanel = ({ title, text, options, footer }) => (
  <Paper variant="outlined" sx={{ p: 1.25, flex: "1 1 260px", minWidth: 0 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {text || <em>(no text, image-only question)</em>}
    </Typography>
    {options?.length > 0 && (
      <Box component="ol" type="A" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
        {options.map((o, i) => (
          <Typography key={i} component="li" variant="caption" sx={{ wordBreak: "break-word" }}>{o}</Typography>
        ))}
      </Box>
    )}
    {footer}
  </Paper>
);

/** One flagged question: this paper's question vs the existing bank question, side by side. */
const QuestionMatchRow = ({ m, onOpen }) => {
  const [open, setOpen] = useState(false);
  const label = `Q${m.paper_number ?? m.index + 1}${m.sub_index != null ? ` (part ${m.sub_index + 1})` : ""}`;
  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setOpen((v) => !v)}>
        <TableCell padding="checkbox">
          <IconButton size="small">{open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}</IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>{label}</TableCell>
        <TableCell sx={{ maxWidth: 360 }}>
          <Typography variant="body2" noWrap title={m.text}>{m.text || m.snippet}</Typography>
          <Typography variant="caption" color="text.secondary">
            matches #{m.question_id}
            {m.existing_category ? ` · ${m.existing_category}` : ""}
            {m.source_type ? ` · ${m.source_type}` : ""}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip size="small" color={m.percent >= 90 ? "error" : "warning"} label={`${Number(m.percent).toFixed(0)}%`} sx={{ fontWeight: 700 }} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <QuestionPanel title={`This paper · ${label}`} text={m.text} options={m.options} />
              <QuestionPanel
                title={`Existing question #${m.question_id}`}
                text={m.existing_text ?? m.snippet}
                options={m.existing_options}
                footer={
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {m.existing_category && <Chip size="small" variant="outlined" label={m.existing_category} sx={{ height: 20, fontSize: "0.65rem" }} />}
                    {m.existing_created_at && (
                      <Typography variant="caption" color="text.secondary">added {format(new Date(m.existing_created_at), "d MMM yyyy")}</Typography>
                    )}
                    <Button size="small" sx={{ textTransform: "none", ml: "auto" }} onClick={() => onOpen(m)}>
                      Open in question bank
                    </Button>
                  </Box>
                }
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Plagiarism / similarity report for a question bank import.
 * Data: GET /admin/question-bank/import/{id}/similarity (ContentSimilarityService on the backend),
 * plus the "On the web" section (WebSimilaritySection → {base}/web-similarity).
 */
const SimilarityReportDialog = ({ open, importId, similarPaperJobId, endpointBase, filename, onClose, onRechecked }) => {
  // Works for an import (uploaded PDF), an AI-generated similar paper, or any entity whose
  // API exposes {endpointBase}/similarity (mock exams, past papers).
  const base = endpointBase
    || (similarPaperJobId
      ? `/admin/question-bank/similar-paper-job/${similarPaperJobId}`
      : `/admin/question-bank/import/${importId}`);
  const targetId = endpointBase || similarPaperJobId || importId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const res = await api.get(`${base}/similarity`);
      setData(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load similarity report");
    } finally {
      setLoading(false);
    }
  }, [base, targetId]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  // While a check is pending, poll until it finishes.
  useEffect(() => {
    if (!open || data?.status !== "pending") return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [open, data, load]);

  const handleRecheck = async () => {
    setRechecking(true);
    try {
      await api.post(`${base}/similarity/recheck`);
      toast.success("Similarity check queued");
      setData((prev) => ({ ...(prev || {}), status: "pending" }));
      onRechecked?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Recheck failed");
    } finally {
      setRechecking(false);
    }
  };

  const report = data?.report;
  const thresholds = report?.thresholds || { warn: 20, high: 50 };
  const percent = data?.percent ?? report?.percent ?? null;
  const colors = similarityColors(percent, thresholds);
  const questionMatches = report?.questions?.matches || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Similarity check
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
          {filename || (similarPaperJobId ? `Similar paper #${similarPaperJobId}` : importId ? `Import #${importId}` : "")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !data ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading report…</Typography>
          </Box>
        ) : data?.status === "pending" ? (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            Similarity check is running. This updates automatically.
          </Alert>
        ) : data?.status === "failed" ? (
          <Alert severity="error">The similarity check failed. Try Recheck.</Alert>
        ) : !report ? (
          <Alert severity="info">
            {data?.status === "skipped"
              ? "This import has no extracted text to compare."
              : "No similarity check has run for this import yet."}
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {report.exact_file_duplicates?.length > 0 && (
              <Alert severity="error">
                This exact PDF file was also uploaded as{" "}
                {report.exact_file_duplicates.map((id) => `#${id}`).join(", ")}. Its extracted questions may since have been
                edited, so the question overlap below can be lower than 100%.
              </Alert>
            )}

            {/* Overall score */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.75 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: colors.color }}>
                  {Number(percent).toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of this paper's content already exists in {report.corpus_size} {similarPaperJobId ? "" : "other "}imported paper{report.corpus_size === 1 ? "" : "s"}
                </Typography>
              </Box>
              {report.original_percent != null && (
                <Alert severity={report.original_percent >= thresholds.high ? "warning" : "info"} sx={{ mb: 1 }}>
                  <strong>{Number(report.original_percent).toFixed(1)}%</strong> copied from its original paper
                  {report.original_import_id ? ` (Import #${report.original_import_id})` : ""}. Comprehension papers often reuse
                  the original passage on purpose; expand the matching paper below to see what was reused.
                </Alert>
              )}
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Number(percent) || 0)}
                color={colors.bar}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                {report.matched_fingerprints} of {report.total_fingerprints} fingerprints matched · {report.word_count} words ·{" "}
                {report.boilerplate_ignored} common-instruction fingerprints ignored · any shared run of{" "}
                {report.algorithm?.guaranteed_match_words ?? 8}+ words is detected
                {data.checked_at && <> · checked {format(new Date(data.checked_at), "d MMM yyyy HH:mm")}</>}
              </Typography>
            </Box>

            {/* Matching papers */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Matching papers</Typography>
              {report.sources?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Paper</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Overlap</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.sources.map((s) => (
                      <SourceRow
                        key={s.import_id}
                        source={s}
                        thresholds={thresholds}
                        onOpenPdf={(id) => navigate(`/admin/question-bank/papers/${id}`)}
                        onReview={(id) => navigate(`/admin/question-bank/import/${id}/review`)}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No overlapping papers found.</Typography>
              )}
            </Box>

            {/* Question-level duplicates (not computed for paper-level-only reports) */}
            {report.questions && <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Questions already in the question bank ({report.questions?.flagged ?? 0} of {report.questions?.checked ?? 0} checked)
              </Typography>
              {questionMatches.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>This paper</TableCell>
                      <TableCell>Question (click a row to compare)</TableCell>
                      <TableCell align="right">Match</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {questionMatches.map((m) => (
                      <QuestionMatchRow
                        key={`${m.index}-${m.sub_index ?? ""}`}
                        m={m}
                        onOpen={(x) => window.open(`/admin/question-bank/${x.parent_question_id || x.question_id}/edit`, "_blank")}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No duplicate questions found.</Typography>
              )}
            </Box>}
          </Box>
        )}

        {/* Other websites (manual, paid searches) — independent of the internal check above */}
        {open && targetId && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <WebSimilaritySection base={base} isImport={!endpointBase && !similarPaperJobId} onChecked={onRechecked} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={rechecking ? <CircularProgress size={14} /> : <Refresh />}
          disabled={rechecking || data?.status === "pending"}
          onClick={handleRecheck}
          sx={{ textTransform: "none", mr: "auto" }}
        >
          Recheck
        </Button>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimilarityReportDialog;
